import React, { useState } from 'react';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  TextField,
  Alert,
  Divider,
  Link,
  Chip,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Schedule as ScheduleIcon,
  Help as HelpIcon,
  DirectionsCar as CarIcon,
  LocationOn as LocationIcon,
  Payment as PaymentIcon,
  ContactSupport as ContactSupportIcon,
  QuestionAnswer as QuestionAnswerIcon,
  Info as InfoIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { useLanguage } from '../contexts/LanguageContext.jsx';
import { useAuth } from '../contexts/AuthContext.jsx';

const HelpPage = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  
  const [contactForm, setContactForm] = useState({
    name: user?.name || '',
    email: user?.email || '',
    subject: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const handleContactFormChange = (field) => (event) => {
    setContactForm(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleContactFormSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);

    try {
      // Simulate form submission
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setContactForm({
        name: user?.name || '',
        email: user?.email || '',
        subject: '',
        message: '',
      });
    } catch (error) {
    } finally {
      setSubmitting(false);
    }
  };

  const faqData = [
    {
      category: 'Booking',
      icon: <CarIcon />,
      questions: [
        {
          question: 'How do I book an MOT test?',
          answer: 'Simply enter your vehicle registration and postcode on our homepage, select a test center, choose your preferred date and time, and complete the payment process. You\'ll receive a confirmation email with all the details.',
        },
        {
          question: 'Can I book an MOT test for someone else?',
          answer: 'Yes, you can book an MOT test for any vehicle as long as you have the registration number. However, the person taking the vehicle for the test should have the necessary documents.',
        },
        {
          question: 'How far in advance can I book an MOT test?',
          answer: 'You can book an MOT test up to 3 months in advance. We recommend booking early, especially during busy periods, to ensure you get your preferred date and time.',
        },
        {
          question: 'What if I can\'t find any available appointments?',
          answer: 'Try expanding your search radius or checking different dates. New appointments are added regularly, so check back frequently. You can also contact our support team for assistance.',
        },
      ],
    },
    {
      category: 'MOT Test Requirements',
      icon: <InfoIcon />,
      questions: [
        {
          question: 'What documents do I need to bring?',
          answer: 'You need to bring your V5C registration document (logbook). If you don\'t have the original V5C, bring the green V5C/2 new keeper supplement or a V62 form.',
        },
        {
          question: 'How long does an MOT test take?',
          answer: 'An MOT test typically takes 45-60 minutes, but this can vary depending on the type of vehicle and any issues found during the test.',
        },
        {
          question: 'What happens if my vehicle fails the MOT?',
          answer: 'If your vehicle fails, you\'ll receive a VT30 refusal certificate listing the reasons for failure. You can get the issues fixed and return for a partial retest, usually at a reduced cost.',
        },
        {
          question: 'Can I drive my car if it fails the MOT?',
          answer: 'You can only drive your vehicle after a failed MOT if your current MOT certificate is still valid, or if you\'re driving to have the faults fixed or to another pre-arranged MOT test.',
        },
      ],
    },
    {
      category: 'Payment & Pricing',
      icon: <PaymentIcon />,
      questions: [
        {
          question: 'How much does an MOT test cost?',
          answer: 'The maximum fee for an MOT test is set by the government. Our prices are competitive and displayed clearly when you select a test center. Additional fees may apply for certain vehicle types.',
        },
        {
          question: 'What payment methods do you accept?',
          answer: 'We accept all major credit and debit cards through our secure payment system powered by Stripe. We do not currently accept cash or cheque payments.',
        },
        {
          question: 'Is there a booking fee?',
          answer: 'Yes, there is a small booking fee of £2.50 to cover the cost of our booking platform and customer support.',
        },
        {
          question: 'Can I get a refund if I cancel my booking?',
          answer: 'You can cancel your booking free of charge up to 24 hours before your appointment. Cancellations within 24 hours may be subject to a cancellation fee.',
        },
      ],
    },
    {
      category: 'Account & Technical',
      icon: <ContactSupportIcon />,
      questions: [
        {
          question: 'Do I need to create an account to book?',
          answer: 'Yes, you need to sign in with your Google account to book an MOT test. This helps us manage your bookings and send you important updates.',
        },
        {
          question: 'How can I view my bookings?',
          answer: 'After signing in, you can view all your bookings by clicking on "My Bookings" in the navigation menu. Here you can see upcoming, past, and cancelled bookings.',
        },
        {
          question: 'I\'m having trouble with the website. What should I do?',
          answer: 'Try refreshing the page or clearing your browser cache. If the problem persists, please contact our support team with details about the issue you\'re experiencing.',
        },
        {
          question: 'How do I change or cancel my booking?',
          answer: 'You can manage your bookings through the "My Bookings" page. Changes and cancellations are allowed up to 24 hours before your appointment time.',
        },
      ],
    },
  ];

  const contactMethods = [
    {
      icon: <PhoneIcon />,
      title: 'Phone Support',
      details: '+44 20 7946 0958',
      description: 'Monday to Friday, 9:00 AM - 6:00 PM',
      action: 'Call Now',
      href: 'tel:+442079460958',
    },
    {
      icon: <EmailIcon />,
      title: 'Email Support',
      details: 'support@motbooking.com',
      description: 'We typically respond within 24 hours',
      action: 'Send Email',
      href: 'mailto:support@motbooking.com',
    },
    {
      icon: <QuestionAnswerIcon />,
      title: 'Live Chat',
      details: 'Chat with our team',
      description: 'Available during business hours',
      action: 'Start Chat',
      onClick: () => {},
    },
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 6 }}>
        <Typography variant="h3" component="h1" sx={{ fontWeight: 'bold', mb: 2 }}>
          {t('help.title')}
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
          Find answers to common questions or get in touch with our support team
        </Typography>
      </Box>

      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' }, 
        gap: 4 
      }}>
        {/* FAQ Section */}
        <Box>
          <Box sx={{ mb: 4 }}>
            <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 3 }}>
              {t('help.faq')}
            </Typography>
            
            {faqData.map((category, categoryIndex) => (
              <Box key={categoryIndex} sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  {category.icon}
                  <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
                    {category.category}
                  </Typography>
                </Box>
                
                {category.questions.map((faq, faqIndex) => (
                  <Accordion key={faqIndex} sx={{ mb: 1 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                        {faq.question}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography variant="body2" color="text.secondary">
                        {faq.answer}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            ))}
          </Box>

          {/* Contact Form */}
          <Card sx={{ mt: 4 }}>
            <CardContent>
              <Typography variant="h5" sx={{ fontWeight: 'medium', mb: 3 }}>
                Still need help? Send us a message
              </Typography>
              
              <Box component="form" onSubmit={handleContactFormSubmit}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <Box sx={{ 
                    display: 'grid', 
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, 
                    gap: 3 
                  }}>
                    <TextField
                      fullWidth
                      label="Your Name"
                      value={contactForm.name}
                      onChange={handleContactFormChange('name')}
                      required
                      disabled={submitting}
                    />
                    
                    <TextField
                      fullWidth
                      label="Email Address"
                      type="email"
                      value={contactForm.email}
                      onChange={handleContactFormChange('email')}
                      required
                      disabled={submitting}
                    />
                  </Box>
                  
                  <TextField
                    fullWidth
                    label="Subject"
                    value={contactForm.subject}
                    onChange={handleContactFormChange('subject')}
                    required
                    disabled={submitting}
                  />
                  
                  <TextField
                    fullWidth
                    label="Message"
                    multiline
                    rows={4}
                    value={contactForm.message}
                    onChange={handleContactFormChange('message')}
                    required
                    disabled={submitting}
                    placeholder="Please describe your issue or question in detail..."
                  />
                  
                  <Box>
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      disabled={submitting}
                      startIcon={<SendIcon />}
                      sx={{ 
                        px: 4, 
                        py: 1.5,
                        '&.Mui-disabled': {
                          backgroundColor: 'primary.main',
                          color: 'white',
                          opacity: 0.8
                        }
                      }}
                    >
                      {submitting ? 'Sending...' : 'Send Message'}
                    </Button>
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Contact Information Sidebar */}
        <Box sx={{ position: 'sticky', top: 20 }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 3 }}>
            {t('help.contact')}
          </Typography>
            
            {contactMethods.map((method, index) => (
              <Card key={index} sx={{ mb: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <Box sx={{ color: 'primary.main', mt: 0.5 }}>
                      {method.icon}
                    </Box>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 'medium', mb: 0.5 }}>
                        {method.title}
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 'medium', mb: 0.5 }}>
                        {method.details}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {method.description}
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        href={method.href}
                        onClick={method.onClick}
                        sx={{ mt: 1 }}
                      >
                        {method.action}
                      </Button>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))}

            <Divider sx={{ my: 3 }} />

            {/* Quick Links */}
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 'medium', mb: 2 }}>
                  Quick Links
                </Typography>
                
                <List dense>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText 
                      primary={
                        <Link href="https://www.gov.uk/getting-an-mot" target="_blank" rel="noopener noreferrer">
                          Official MOT Information
                        </Link>
                      }
                    />
                  </ListItem>
                  
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText 
                      primary={
                        <Link href="https://www.gov.uk/check-mot-history" target="_blank" rel="noopener noreferrer">
                          Check MOT History
                        </Link>
                      }
                    />
                  </ListItem>
                  
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText 
                      primary={
                        <Link href="/terms" target="_blank" rel="noopener noreferrer">
                          Terms & Conditions
                        </Link>
                      }
                    />
                  </ListItem>
                  
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText 
                      primary={
                        <Link href="/privacy" target="_blank" rel="noopener noreferrer">
                          Privacy Policy
                        </Link>
                      }
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>

            {/* Business Hours */}
            <Paper sx={{ p: 2, mt: 3, backgroundColor: 'primary.50' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <ScheduleIcon color="primary" fontSize="small" />
                <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
                  Business Hours
                </Typography>
              </Box>
              <Typography variant="body2">
                Monday - Friday: 9:00 AM - 6:00 PM<br />
                Saturday: 10:00 AM - 4:00 PM<br />
                Sunday: Closed
              </Typography>
            </Paper>

            {/* Emergency Notice */}
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Emergency Support:</strong> For urgent issues outside business hours, 
                please email us and we'll respond as soon as possible.
              </Typography>
            </Alert>
        </Box>
      </Box>
    </Container>
  );
};

export default HelpPage;